import { createApi } from '@reduxjs/toolkit/query/react';
import * as v from 'valibot';

import { REACT_APP_BE_API_URL } from '#shared';

import {
  asQueryFnResult,
  fetchWithAuthBaseQuery,
  requestJsonWithAuth,
  validateAndTransformResponse,
} from '#shared/api';

import {
  activityMonitoringSchema,
  activitySeriesSchema,
} from './schemas/activitySeries.schema';
import { adminUsersResponseSchema } from './schemas/adminUser.schema';

import type {
  ActivityMonitoring,
  ActivitySeries,
  AdminUser,
} from '../model/types';

export interface UserActivityArgs {
  oid: string;
  /** ISO 8601 with a Z or an offset — the BE 400s on an offset-less string. */
  from: string;
  to: string;
  /** One of the BE's closed enum: 1m | 5m | 15m | 1h. */
  bucket: string;
}

export interface SetMonitoringInput {
  oid: string;
  reason: string;
  expiresAt: string;
}

const monitoringPath = (oid: string) =>
  `api/admin/users/${encodeURIComponent(oid)}/activity-monitoring`;

/**
 * Fold a settled toggle into the roster cache.
 *
 * ON SUCCESS ONLY, never optimistically: `activityMonitoringSetBy` is the
 * acting admin's oid taken from the token server-side, so an optimistic patch
 * would have to invent it — and the BE still rejects a bad reason or expiry
 * with a 400 and an off-roster oid with a 404. The response carries exactly the
 * five fields the row needs, which is what the BE shaped it for, so no refetch
 * is required.
 *
 * This repo has no RTK Query tags at all; manual updateQueryData is the only
 * cache-write mechanism (see processes/nop-events/applyPresenceFrame.ts).
 */
const applyMonitoring =
  (updated: ActivityMonitoring) => (draft: AdminUser[]) => {
    const row = draft.find(user => user.oid === updated.oid);
    if (!row) {
      return;
    }
    row.isActivityMonitored = updated.isActivityMonitored;
    row.activityMonitoringReason = updated.activityMonitoringReason;
    row.activityMonitoringSetBy = updated.activityMonitoringSetBy;
    row.activityMonitoringExpiresAt = updated.activityMonitoringExpiresAt;
  };

/**
 * The admin panel's user roster (TMDXNC-241). Admin-gated on the BE
 * (`@Roles('Admin')`) — a non-admin gets a 403, which surfaces as the query's
 * error state; the route is admin-gated too, so that is belt and braces.
 *
 * The `online` flags in this cache are ALSO written by the live presence
 * frames — see processes/nop-events/applyPresenceFrame.ts.
 */
export const adminUsersApi = createApi({
  reducerPath: 'adminUsersApi',
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnReconnect: true,

  endpoints: build => ({
    getAdminUsers: build.query<AdminUser[], void>({
      query: () => 'api/admin/users',
      // The BE returns a bare array; wrap it into the ResponseResult envelope
      // the repo's one validation mechanism expects (same move as
      // nopResources.api.ts getNopCSShifts).
      transformResponse: (response: AdminUser[]) =>
        validateAndTransformResponse(
          adminUsersResponseSchema,
          {
            data: response,
            errors: null,
            expires: null,
            dataGeneratedAt: null,
            errorsGeneratedAt: null,
          },
          value => value?.oid,
          'adminUsers',
        ).data,
    }),

    getUserActivity: build.query<ActivitySeries, UserActivityArgs>({
      query: ({ oid, from, to, bucket }) =>
        `api/admin/users/${encodeURIComponent(oid)}/activity` +
        `?from=${encodeURIComponent(from)}` +
        `&to=${encodeURIComponent(to)}` +
        `&bucket=${encodeURIComponent(bucket)}`,
      // `v.parse`, NOT validateAndTransformResponse. DATA-001's machinery grades
      // ITEMS so a bad one can be DROPPED and the rest kept — but a dropped
      // bucket is indistinguishable from an absent one, and an absent bucket IS
      // the gap this series exists to show. Dropping would fabricate downtime,
      // so a malformed payload has to throw.
      transformResponse: (response: unknown) =>
        v.parse(activitySeriesSchema, response),
    }),

    setActivityMonitoring: build.mutation<
      ActivityMonitoring,
      SetMonitoringInput
    >({
      // `queryFn`, not `query`: fetchWithAuthBaseQuery takes a bare string URL
      // and is a hardcoded GET, so a POST cannot be expressed through it — the
      // same reason adminEvents.api.ts reaches for queryFn.
      queryFn: ({ oid, reason, expiresAt }) =>
        asQueryFnResult(async () => {
          const updated = await requestJsonWithAuth<unknown>(
            `${REACT_APP_BE_API_URL}${monitoringPath(oid)}`,
            { method: 'POST', json: { reason, expiresAt } },
          );
          return v.parse(activityMonitoringSchema, updated);
        }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        // Swallowed deliberately, and only here: a rejection already reaches the
        // caller through the mutation's own promise (the query hooks unwrap it),
        // so re-throwing from this side-channel would surface the SAME failure a
        // second time as an unhandled rejection. A failed toggle has nothing to
        // patch.
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminUsersApi.util.updateQueryData(
              'getAdminUsers',
              undefined,
              applyMonitoring(data),
            ),
          );
        } catch {
          return;
        }
      },
    }),

    clearActivityMonitoring: build.mutation<ActivityMonitoring, string>({
      queryFn: oid =>
        asQueryFnResult(async () => {
          const updated = await requestJsonWithAuth<unknown>(
            `${REACT_APP_BE_API_URL}${monitoringPath(oid)}`,
            { method: 'DELETE' },
          );
          return v.parse(activityMonitoringSchema, updated);
        }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        // Swallowed deliberately, and only here: a rejection already reaches the
        // caller through the mutation's own promise (the query hooks unwrap it),
        // so re-throwing from this side-channel would surface the SAME failure a
        // second time as an unhandled rejection. A failed toggle has nothing to
        // patch.
        try {
          const { data } = await queryFulfilled;
          dispatch(
            adminUsersApi.util.updateQueryData(
              'getAdminUsers',
              undefined,
              applyMonitoring(data),
            ),
          );
        } catch {
          return;
        }
      },
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useGetUserActivityQuery,
  useSetActivityMonitoringMutation,
  useClearActivityMonitoringMutation,
} = adminUsersApi;
